import React, { type ReactElement, useState } from 'react';
import { InterPoint } from '@saviynt/common';
import {
  Box,
  ButtonIcon,
  Icon,
  Link as DSLink,
  ListRow,
  ListRowGroup,
  Menu,
  Typography,
} from '@saviynt/design-system';

import {
  CF_NEU_BLD,
  CF_NEU_MED,
  CF_NEU_STL,
  CF_SEC_MED,
  ENTITLEMENT_DEATILS_TABS,
  type EntitlementRowType,
  violationsEntitlement,
} from '../../../../shared/constants';
import { useInvertedLocalize, useNavigateTo } from '../../../../shared/hooks';

import './SodEntitlementListGroup.css';

const initialOptionsWithIcons = [
  {
    label: 'Move to “In Review” (360)',
    value: 'move',
    icon: 'Comment',
  },
  {
    label: 'Deactivate Entitlement',
    value: 'reject',
    icon: 'Reject',
  },
  {
    label: 'Revoke (24)',
    value: 'terminate',
    icon: 'Terminate',
  },
  {
    label: 'Generate Report',
    value: 'report',
    icon: 'Report',
  },
  {
    label: 'MenuOptionDivider',
    value: 'menuOptionDivider',
    isDivider: true,
  },
  {
    label: 'View Detail',
    value: 'manageAll',
    icon: 'ArrowRight',
  },
];

interface SodEntitlementSidePanelProps {
  handleOpenSidePanel: (item: EntitlementRowType) => void;
  selectedRow: EntitlementRowType | null;
  listRows: EntitlementRowType[];
}

/**
 * * TODO
 * - Write a description
 * - applicationComp in rows need to be a component, maybe Sagar is making this?
 * - Localize Menu Option Labels
 */
function SodEntitlementListGroup({
  handleOpenSidePanel,
  selectedRow,
  listRows,
}: SodEntitlementSidePanelProps): ReactElement | null {
  const [expandedMenuId, setExpandedMenuId] = useState<number | null>(null);
  const l = useInvertedLocalize();

  const { navigateToDetails } = useNavigateTo();

  const getCenterContent = (item: EntitlementRowType) => {
    const { title, role, applicationComp, description } = item;

    return (
      <Box className='SodEntitlementListGroup-centerContent'>
        <Typography kind='body3' color={CF_SEC_MED}>
          {l('Entitlement')}
        </Typography>
        <Typography kind='h3' color={CF_NEU_BLD}>
          {title}
        </Typography>
        <Box className='SodEntitlementListGroup-rowDetails'>
          <Typography kind='body2' color={CF_NEU_STL}>
            {role}
          </Typography>
          <InterPoint color='neutralBold' font='body2' />
          <Typography kind='h2' color={CF_NEU_BLD} isNoWrap>
            {applicationComp}
          </Typography>
          <InterPoint color='neutralBold' font='body2' />
          <Typography kind='body2' color={CF_NEU_STL} lineLimit={1}>
            {description}
          </Typography>
        </Box>
      </Box>
    );
  };

  const getTrailingContent = (item: EntitlementRowType) => {
    const { inherentRisks, users, violations } = item;

    const isExpanded = expandedMenuId === item.id;

    const menuTrigger = (
      <ButtonIcon
        size='medium'
        kind='ghost'
        isSelected={isExpanded}
        icon={<Icon kind='MoreVertical' />}
      />
    );

    const handleMenuExpand = (isMenuOpen: boolean) => {
      if (isMenuOpen) {
        setExpandedMenuId(null);
      } else {
        setExpandedMenuId(item.id);
      }
    };

    const handleSourceChange = (sourceType: string, activeTab: string) => {
      navigateToDetails({
        key: '1234567890',
        params: {
          title: 'OracleCloud_GL_SOD_Enter Journal & Post Journal',
          user: 'Joey Kookamunga',
          type: sourceType,
          activeTab,
        },
      });
    };

    return (
      <Box className='SodEntitlementListGroup-trailingContent'>
        <Box className='SodEntitlementListGroup-rowLinkBlocks'>
          <Box className='SodEntitlementListGroup-rowLinkBlock'>
            <Typography kind='body3' color={CF_NEU_STL} isNoWrap>
              {l('Inherent Risks')}
            </Typography>
            <DSLink
              kind='button'
              onClick={() => {
                handleSourceChange(
                  violationsEntitlement,
                  ENTITLEMENT_DEATILS_TABS.TAB_RISKS
                );
              }}
              text={String(inherentRisks)}
              size='large'
              prefixIconKind='CriticalHighRiskHexagon'
            />
          </Box>
          <Box className='SodEntitlementListGroup-rowLinkBlock'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Users')}
            </Typography>
            <DSLink
              kind='button'
              onClick={() => {
                handleSourceChange(
                  violationsEntitlement,
                  ENTITLEMENT_DEATILS_TABS.TAB_USERS
                );
              }}
              text={String(users)}
              size='large'
              prefixIconKind='User'
            />
          </Box>
          <Box className='SodEntitlementListGroup-rowLinkBlock'>
            <Typography kind='body3' color={CF_NEU_STL}>
              {l('Violations')}
            </Typography>
            <DSLink
              kind='button'
              onClick={() => {
                handleSourceChange(
                  violationsEntitlement,
                  ENTITLEMENT_DEATILS_TABS.TAB_VIOLATIONS
                );
              }}
              text={String(violations)}
              size='large'
              prefixIconKind='SOD'
            />
          </Box>
        </Box>
        {/* Placeholder Button */}
        <Box className='SodEntitlementListGroup-rowKabobWrapper'>
          <Menu
            options={initialOptionsWithIcons}
            onChange={() => {}}
            minOptionsForSearch={10}
            trigger={menuTrigger}
            setIsExpanded={(isMenuOpen: boolean) => {
              handleMenuExpand(isMenuOpen);
            }}
            placement='bottom-end'
            isPortalEnabled
          />
        </Box>
      </Box>
    );
  };

  if (listRows.length === 0) {
    return (
      <Box className='SodEntitlementListGroup-emptyList'>
        <Typography kind='body1' color={CF_NEU_MED}>
          {l('No records match your search')}
        </Typography>
      </Box>
    );
  }

  return (
    <ListRowGroup className='SodEntitlementListGroup'>
      {listRows.map((item) => (
        <ListRow
          key={item.id}
          centerContent={getCenterContent(item)}
          leadingComp={
            <Icon
              kind='Entitlement'
              color='--color-foreground-secondary-medium'
            />
          }
          trailingContent={getTrailingContent(item)}
          isCheckboxDisabled
          handleRowClick={() => handleOpenSidePanel(item)}
          isRowDetailsOpen={selectedRow?.id === item.id}
          connectorDistance='1.625rem'
        />
      ))}
    </ListRowGroup>
  );
}

export default SodEntitlementListGroup;
